import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Pressable, ScrollView, Text, TextInput, TextStyle, View, ViewStyle } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faArrowRight, faKeyboard, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { NavigationProp } from '@react-navigation/native';
import { RootStackParamList } from '../typings/Params';
import { IconDefinition as ProIconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { translate } from '../services/translate';
import { RootNavigation } from '../Routes';

type WebStyle = ViewStyle & TextStyle & { cursor?: string; outlineStyle?: string; };

export interface SystemPage {
    key: string;
    icon?: IconDefinition | ProIconDefinition;
    label: string;
    module?: string;
    description?: string;
    navigate: (navigation: RootNavigation) => void;
}

interface Props {
    visible: boolean;
    onClose: () => void;
    navigation: NavigationProp<RootStackParamList> | RootNavigation;
    pages: SystemPage[];
}

function GlobalSearchModal({ visible, onClose, navigation, pages }: Props) {
    const { styles, theme } = useStyles(stylesheet);
    const [query, setQuery] = useState('');
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);
    const inputRef = useRef<TextInput>(null);
    const scrollRef = useRef<ScrollView>(null);

    useEffect(() => {
        if (visible) {
            setQuery('');
            setFocusedIndex(-1);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [visible]);

    const allModules = useMemo(() => {
        const seen = new Set<string>();
        const result: SystemPage[] = [];
        for (const page of pages) {
            if (page.module && !seen.has(page.module)) {
                seen.add(page.module);
                result.push(page);
            }
        }
        return result;
    }, [pages]);

    const filteredModules = useMemo(() => {
        const q = query
            .trim()
            .toLocaleLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        if (!q) {
            return [];
        }
        return allModules.filter(p => p.module && p.module.toLocaleLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .includes(q));
    }, [query, allModules]);

    const filteredPages = useMemo(() => {
        const q = query.trim().toLocaleLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        if (!q) {
            return pages;
        }
        return pages.filter(
            p =>
                p.label
                    .toLocaleLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .includes(q) ||

                (p.module && p.module
                    .toLocaleLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .includes(q)) ||

                (p.description ?? '')
                    .toLocaleLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .includes(q),
        );
    }, [query, pages]);

    const flatList = useMemo(
        () => [...filteredModules, ...filteredPages],
        [filteredModules, filteredPages],
    );

    const handleNavigate = useCallback(
        (page: SystemPage) => {
            onClose();
            const nav = navigation as unknown as RootNavigation;
            page.navigate(nav);
        },
        [navigation, onClose],
    );

    const flatListRef = useRef(flatList);
    const focusedIndexRef = useRef(focusedIndex);

    useEffect(() => {
        flatListRef.current = flatList;
    }, [flatList]);

    useEffect(() => {
        focusedIndexRef.current = focusedIndex;
    }, [focusedIndex]);

    useEffect(() => {
        if (!visible) {
            return;
        }

        const onKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setFocusedIndex(i => Math.min(i + 1, flatListRef.current.length - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setFocusedIndex(i => Math.max(i - 1, 0));
                    break;
                case 'Enter': {
                    const idx = focusedIndexRef.current;
                    if (idx >= 0 && flatListRef.current[idx]) {
                        handleNavigate(flatListRef.current[idx]);
                    }
                    break;
                }
                case 'Escape':
                    onClose();
                    break;
            }
        };

        document.addEventListener('keydown', onKeyDown, { capture: true });
        return () => document.removeEventListener('keydown', onKeyDown, { capture: true });
    }, [visible, handleNavigate, onClose]);

    const flatIndexMap = useMemo(() => {
        const map = new Map<string, number>();
        let idx = 0;
        for (const mod of filteredModules) {
            map.set(`module:${mod.module}`, idx++);
        }
        for (const page of filteredPages) {
            map.set(page.key, idx++);
        }
        return map;
    }, [filteredModules, filteredPages]);

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onClose}
        >
            <Pressable style={styles.backdrop} onPress={onClose}>
                <Pressable style={styles.modalContainer} onPress={e => e.stopPropagation()}>

                    <View style={styles.searchRow}>
                        <FontAwesomeIcon
                            icon={faSearch as IconDefinition}
                            fontSize={16}
                            color={theme.colors.placeholder}
                            style={styles.searchIcon}
                        />
                        <TextInput
                            ref={inputRef}
                            style={styles.searchInput}
                            value={query}
                            onChangeText={setQuery}
                            placeholder={translate('searchModulesAndPages')}
                            placeholderTextColor={theme.colors.placeholder}
                            autoCorrect={false}
                            autoCapitalize="none"
                        />
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <Text style={styles.closeText}>✕</Text>
                        </Pressable>
                    </View>

                    <View style={styles.divider} />

                    <ScrollView
                        ref={scrollRef}
                        style={styles.resultsList}
                        keyboardShouldPersistTaps="handled"
                        showsVerticalScrollIndicator={false}
                    >
                        {filteredModules.length === 0 && filteredPages.length === 0 ? (
                            <View style={styles.emptyState}>
                                <Text style={styles.emptyText}>{translate('noResultsFor')} &ldquo;{query}&rdquo;</Text>
                            </View>
                        ) : (
                            <>
                                {filteredModules.length > 0 && (
                                    <View style={styles.group}>
                                        <Text style={styles.groupHeader}>{translate('systemModules')}</Text>
                                        {filteredModules.map(mod => {
                                            const flatIdx = flatIndexMap.get(`module:${mod.module}`) ?? -1;
                                            const isFocused = flatIdx === focusedIndex;
                                            return (
                                                <Pressable
                                                    key={`module:${mod.module}`}
                                                    style={[styles.resultItem, isFocused && styles.resultItemFocused]}
                                                    onPress={() => handleNavigate(mod)}
                                                    onHoverIn={() => setFocusedIndex(flatIdx)}
                                                    onHoverOut={() => setFocusedIndex(-1)}
                                                >
                                                    <View style={styles.resultLeft}>
                                                        <FontAwesomeIcon
                                                            icon={faLayerGroup as IconDefinition}
                                                            fontSize={14}
                                                            color={isFocused ? theme.colors.iconColor : theme.colors.placeholder}
                                                            style={styles.resultIcon}
                                                        />
                                                        <Text style={[styles.resultLabel, isFocused && styles.resultLabelFocused]}>
                                                            {mod.module}
                                                        </Text>
                                                    </View>
                                                    <View style={styles.jumpBadge}>
                                                        <Text style={styles.jumpText}>{translate('goTo')}</Text>
                                                        <FontAwesomeIcon
                                                            icon={faArrowRight as IconDefinition}
                                                            fontSize={11}
                                                            color={theme.colors.placeholder}
                                                        />
                                                    </View>
                                                </Pressable>
                                            );
                                        })}
                                    </View>
                                )}

                                {filteredModules.length > 0 && filteredPages.length > 0 && (
                                    <View style={styles.sectionDivider} />
                                )}

                                {filteredPages.length > 0 && (
                                    <View style={styles.group}>
                                        <Text style={styles.groupHeader}>{translate('systemMenus')}</Text>
                                        {filteredPages.map(page => {
                                            const flatIdx = flatIndexMap.get(page.key) ?? -1;
                                            const isFocused = flatIdx === focusedIndex;
                                            return (
                                                <Pressable
                                                    key={page.key}
                                                    style={[styles.resultItem, isFocused && styles.resultItemFocused]}
                                                    onPress={() => handleNavigate(page)}
                                                    onHoverIn={() => setFocusedIndex(flatIdx)}
                                                    onHoverOut={() => setFocusedIndex(-1)}
                                                >
                                                    <View style={styles.resultLeft}>
                                                        <View style={{ minWidth: 20 }}>
                                                            <FontAwesomeIcon
                                                                icon={page.icon as IconDefinition ?? faKeyboard as IconDefinition}
                                                                fontSize={14}
                                                                color={isFocused ? theme.colors.iconColor : theme.colors.placeholder}
                                                                style={styles.resultIcon}
                                                            />
                                                        </View>
                                                        <View>
                                                            <Text style={[styles.resultLabel, isFocused && styles.resultLabelFocused]}>
                                                                {page.label}
                                                            </Text>
                                                            {page.module &&
                                                                <Text style={styles.resultDescription}>
                                                                    {translate('module')} / {page.module}
                                                                </Text>
                                                            }
                                                        </View>
                                                    </View>
                                                    <View style={styles.jumpBadge}>
                                                        <Text style={styles.jumpText}>{translate('goTo')}</Text>
                                                        <FontAwesomeIcon
                                                            icon={faArrowRight as IconDefinition}
                                                            fontSize={11}
                                                            color={theme.colors.placeholder}
                                                        />
                                                    </View>
                                                </Pressable>
                                            );
                                        })}
                                    </View>
                                )}
                            </>
                        )}
                    </ScrollView>

                </Pressable>
            </Pressable>
        </Modal >
    );
}

const stylesheet = createStyleSheet(theme => ({
    backdrop: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.55)',
        alignItems: 'center',
        paddingTop: 60,
    },

    modalContainer: {
        width: '100%',
        maxWidth: 680,
        backgroundColor: theme.colors.topMenu,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.field?.border ?? '#333',
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.4,
        shadowRadius: 24,
        elevation: 20,
    },

    searchRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        height: 56,
        gap: 10,
    },

    searchIcon: {
        flexShrink: 0,
    },

    searchInput: {
        flex: 1,
        fontSize: 16,
        color: theme.colors.field?.text ?? '#FFF',
        fontFamily: 'Open Sans',
        outlineStyle: 'none',
    } satisfies WebStyle,

    closeButton: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: theme.colors.field?.background ?? 'rgba(255,255,255,0.08)',
        justifyContent: 'center',
        alignItems: 'center',
    },

    closeText: {
        fontSize: 14,
        color: theme.colors.placeholder,
        fontFamily: 'Open Sans',
        lineHeight: 14,
    },

    divider: {
        height: 1,
        backgroundColor: theme.colors.field?.border ?? '#333',
    },

    sectionDivider: {
        height: 1,
        backgroundColor: theme.colors.field?.border ?? '#333',
        marginVertical: 4,
        marginHorizontal: 16,
    },

    resultsList: {
        maxHeight: 420,
    },

    group: {
        paddingBottom: 4,
    },

    groupHeader: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.placeholder,
        fontFamily: 'Open Sans',
        paddingHorizontal: 16,
        paddingTop: 14,
        paddingBottom: 6,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
    },

    resultItem: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 10,
        cursor: 'pointer',
    } satisfies WebStyle,

    resultItemFocused: {
        backgroundColor: theme.colors.field?.background ?? 'rgba(255,255,255,0.06)',
    },

    resultLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },

    resultIcon: {
        flexShrink: 0,
    },

    resultLabel: {
        fontSize: 14,
        color: theme.colors.field?.text ?? '#CCC',
        fontFamily: 'Open Sans',
    },

    resultLabelFocused: {
        color: theme.colors.iconColor ?? '#FFF',
    },

    resultDescription: {
        fontSize: 12,
        color: theme.colors.placeholder,
        fontFamily: 'Open Sans',
        marginTop: 1,
    },

    jumpBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },

    jumpText: {
        fontSize: 12,
        color: theme.colors.placeholder,
        fontFamily: 'Open Sans',
    },

    emptyState: {
        paddingVertical: 40,
        alignItems: 'center',
    },

    emptyText: {
        fontSize: 14,
        color: theme.colors.placeholder,
        fontFamily: 'Open Sans',
    },
}));

export default GlobalSearchModal;
